import React from "react";
import { Container, Typography, Box, Paper, Divider } from "@mui/material";
import { Helmet } from "react-helmet-async";
import AnalyticsTracker from "../GASetup/AnalyticsTracker";
import {
  cardOutline,
  fonts,
  headingFonts,
  semiBoldFonts,
} from "../../Styles/Styles";

const PrivacyPolicy = () => {
  return (
    <Container
      maxWidth="md"
      sx={{
        mt: 5,
        mb: 5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Helmet>
        <title>Privacy Policy - Listed.ky</title>
        <meta name="description" content="Privacy Policy - Listed.ky" />
      </Helmet>
      <AnalyticsTracker />

      {/* Page Title */}
      <Typography
        variant="h3"
        sx={{
          mb: 2,
          ...semiBoldFonts,
          letterSpacing: 2,
          color: "var(--primary-color)",
          textTransform: "uppercase",
          position: "relative",
          display: "inline-block",
          paddingBottom: "8px",
          "&::after": {
            content: '""',
            width: "80px",
            height: "4px",
            backgroundColor: "var(--primary-color)",
            position: "absolute",
            bottom: 0,
            left: "50%",
            transform: "translateX(-50%)",
          },
        }}
      >
        Privacy Policy
      </Typography>

      {/* Privacy Policy Card */}
      <Paper
        elevation={3}
        sx={{
          padding: 4,
          borderRadius: "12px",
          backgroundColor: "white",
          ...cardOutline,
        }}
      >
        {[
          {
            title: "1. Introduction",
            content:
              "Welcome to Listed.ky. Your privacy is important to us. This Privacy Policy outlines how we collect, use, disclose, and protect your personal information when you visit our website and use our services. By using Listed.ky, you agree to the terms of this Privacy Policy, which is governed by the laws of the Cayman Islands.",
          },
          {
            title: "2. Information We Collect",
            content:
              "We do not collect payment information. We may collect the following types of information to provide and improve our services:",
            list: [
              "Personal Information: When you create an account, list an item, or contact us, we may collect personal details such as your name, email address, and phone number.",
            ],
          },
          {
            title: "3. Use of Information",
            content:
              "Listed.ky uses the collected information for various purposes, including:",
            list: [
              "To provide and maintain our services",
              "To notify you about changes to our services",
              "To allow you to participate in interactive features of our services when you choose to do so",
              "To provide customer support",
              "To gather analysis or valuable information to improve our website",
            ],
          },
          {
            title: "4. Data Security",
            content:
              "We prioritize the security of your data. We implement reasonable security measures, including encrypted backups, to protect your personal information from unauthorized access, use, alteration, or disclosure. We do not store personal data directly, ensuring your privacy is maintained.",
          },
          {
            title: "5. Disclosure of Information",
            content:
              "We may disclose your personal information in the following situations:",
            list: [
              "Legal Requirements: We may disclose your information if required to do so by court order in the Cayman Islands.",
              "Ad Tracking: We may share information for Google ad tracking to provide targeted advertisements.",
            ],
          },
          {
            title: "6. Your Rights",
            content:
              "Depending on applicable laws, you may have rights regarding your personal information, including:",
            list: [
              "The right to access your personal information",
              "The right to rectify any inaccurate or incomplete information",
              "The right to request the deletion of your personal information",
            ],
          },
          {
            title: "7. Changes to This Privacy Policy",
            content:
              "We reserve the right to update our Privacy Policy at any time without prior notification. We encourage you to review this Privacy Policy periodically to stay informed about any changes. Your continued use of Listed.ky after any modifications to the Privacy Policy will constitute your acknowledgment of the changes and your consent to abide by and be bound by the modified policy.",
          },
          {
            title: "8. Contact Us",
            content:
              "If you have any questions about this Privacy Policy, please contact us:",
            list: [
              'Email: <a href="mailto:privacy@listed.ky">privacy@listed.ky</a>',
            ],
          },
        ].map((section, index) => (
          <Box key={index} sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              sx={{ ...headingFonts, color: "var(--primary-color)" }}
            >
              {section.title}
            </Typography>
            <Typography
              paragraph
              sx={{
                ...fonts,
                color: "text.secondary",
                mt: 1,
              }}
              dangerouslySetInnerHTML={{ __html: section.content }}
            />
            {section.list && (
              <ul>
                {section.list.map((item, idx) => (
                  <li key={idx} style={{ ...fonts, color: "rgb(0 0 0 / 60%)" }}>
                    <span dangerouslySetInnerHTML={{ __html: item }} />
                  </li>
                ))}
              </ul>
            )}
            {index !== 7 && (
              <Divider sx={{ my: 3, borderColor: "rgba(0, 0, 0, 0.1)" }} />
            )}
          </Box>
        ))}
      </Paper>
    </Container>
  );
};

export default PrivacyPolicy;
